import React, { useState, useEffect, useCallback } from 'react';
import { 
  EyeIcon, 
  TrashIcon
} from '@heroicons/react/24/outline';
import { 
  Table, 
  Button, 
  Modal, 
  Input, 
  Select, 
  Popconfirm, 
  message, 
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip
} from 'antd';
import { ContactMessage } from '../../types';
import apiService from '../../services/api';


const { Option } = Select;

const ContactMessageManagement: React.FC = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    replied: 0,
    processed: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '全部',
    search: ''
  });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔍 开始获取联系消息...');
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
        ...(filters.status !== '全部' && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      });

      console.log('📡 API请求URL:', `/contact-messages?${params}`);
      const response = await apiService.get<{
        data: ContactMessage[];
        pagination: { current: number; pageSize: number; total: number };
      }>(`/contact-messages?${params}`);
      
      console.log('📥 API响应:', response);
      
      if (response.success) {
        console.log('✅ 获取消息成功，数据:', response.data);
        console.log('🔍 response.data类型:', typeof response.data);
        console.log('🔍 response.data是否为数组:', Array.isArray(response.data));
        console.log('🔍 response.data.data:', response.data?.data);
        console.log('🔍 response.data.data类型:', typeof response.data?.data);
        console.log('🔍 response.data.data是否为数组:', Array.isArray(response.data?.data));
        
        // 检查数据结构
        let messagesData: ContactMessage[];
        let paginationData: any;
        
        if (Array.isArray(response.data)) {
          // 如果response.data本身就是数组
          messagesData = response.data;
          paginationData = { total: response.data.length };
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // 如果response.data.data是数组
          messagesData = response.data.data;
          paginationData = response.data.pagination;
        } else {
          // 其他情况
          messagesData = [];
          paginationData = { total: 0 };
        }
        
        console.log('📋 最终设置的消息数据:', messagesData);
        console.log('📊 分页数据:', paginationData);
        setMessages(messagesData);
        setPagination(prev => ({
          ...prev,
          total: paginationData?.total || 0
        }));
      } else {
        console.error('❌ API返回失败:', response);
        message.error(response.message || '获取联系消息失败');
      }
    } catch (error: any) {
      console.error('❌ 获取联系消息异常:', error);
      if (error.response) {
        console.error('错误状态码:', error.response.status);
        console.error('错误响应:', error.response.data);
      }
      message.error('获取联系消息失败');
    } finally {
      setLoading(false);
    }
  }, [pagination, filters]);

  const fetchStats = useCallback(async () => {
    try {
      console.log('🔍 开始获取统计信息...');
      const response = await apiService.get<{
        total: number;
        unread: number;
        read: number;
        replied: number;
        processed: number;
      }>('/contact-messages/stats/summary');
      
      console.log('📥 统计API响应:', response);
      
      if (response.success) {
        console.log('✅ 获取统计成功，数据:', response.data);
        const statsData = response.data || {
          total: 0,
          unread: 0,
          read: 0,
          replied: 0,
          processed: 0
        };
        console.log('📊 设置统计数据:', statsData);
        setStats(statsData);
      } else {
        console.error('❌ 统计API返回失败:', response);
      }
    } catch (error: any) {
      console.error('❌ 获取统计信息异常:', error);
      if (error.response) {
        console.error('错误状态码:', error.response.status);
        console.error('错误响应:', error.response.data);
      }
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [fetchMessages, fetchStats]);

  const handleViewMessage = async (message: ContactMessage) => {
    setSelectedMessage(message);
    setModalVisible(true);
    
    // 如果消息未读，标记为已读
    if (!message.isRead) {
      try {
        await apiService.patch(`/contact-messages/${message._id}/read`, {});
        // 更新本地状态
        setMessages(prev => prev.map(msg => 
          msg._id === message._id 
            ? { ...msg, isRead: true, status: '已读' }
            : msg
        ));
        fetchStats(); // 刷新统计
      } catch (error) {
        console.error('标记已读失败:', error);
      }
    }
  };

  const handleStatusChange = async (messageId: string, status: string) => {
    try {
      await apiService.patch(`/contact-messages/${messageId}/status`, { status });
      message.success('状态更新成功');
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('状态更新失败:', error);
      message.error('状态更新失败');
    }
  };

  const handleDelete = async (messageId: string) => {
    try {
      await apiService.delete(`/contact-messages/${messageId}`);
      message.success('删除成功');
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">联系我们管理</h1>
          <p className="text-gray-600 mt-1">管理来自官网联系表单的消息</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16}>
        <Col span={4}>
          <Card>
            <Statistic
              title="总消息数"
              value={stats.total}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="未读消息"
              value={stats.unread}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已读消息"
              value={stats.read}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已回复"
              value={stats.replied}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已处理"
              value={stats.processed}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选器 */}
      <Card>
        <div className="flex space-x-4">
          <Select
            style={{ width: 120 }}
            value={filters.status}
            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
          >
            <Option value="全部">全部状态</Option>
            <Option value="未读">未读</Option>
            <Option value="已读">已读</Option>
            <Option value="已回复">已回复</Option>
            <Option value="已处理">已处理</Option>
          </Select>
          <Input.Search
            placeholder="搜索姓名、邮箱、主题或内容"
            style={{ width: 300 }}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onSearch={(value) => setFilters(prev => ({ ...prev, search: value }))}
          />
        </div>
      </Card>

      {/* 消息列表 */}
      <Card>
        <Table
          columns={[
            {
              title: '状态',
              key: 'status',
              width: 100,
              render: (message: ContactMessage) => (
                <Tag color={
                  message.status === '未读' ? 'red' :
                  message.status === '已读' ? 'blue' :
                  message.status === '已回复' ? 'green' : 'purple'
                }>
                  {message.status}
                </Tag>
              )
            },
            {
              title: '姓名',
              dataIndex: 'name',
              key: 'name',
              width: 120,
              render: (text: string) => (
                <span className="font-medium">{text}</span>
              )
            },
            {
              title: '邮箱',
              dataIndex: 'email',
              key: 'email',
              width: 200,
              render: (text: string) => (
                <span className="text-blue-600">{text}</span>
              )
            },
            {
              title: '主题',
              dataIndex: 'subject',
              key: 'subject',
              width: 200,
              render: (text: string) => (
                <span className="truncate block">{text}</span>
              )
            },
            {
              title: '消息内容',
              dataIndex: 'message',
              key: 'message',
              width: 300,
              render: (text: string, record: ContactMessage) => (
                <div className="max-w-xs">
                  <div className="text-gray-600 text-sm line-clamp-3 mb-2">
                    {text.length > 150 ? `${text.substring(0, 150)}...` : text}
                  </div>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleViewMessage(record)}
                    className="p-0 h-auto text-blue-600 hover:text-blue-800"
                  >
                    查看完整内容
                  </Button>
                </div>
              )
            },
            {
              title: '提交时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 180,
              render: (text: string) => (
                <span className="text-sm text-gray-500">
                  {new Date(text).toLocaleString('zh-CN')}
                </span>
              )
            },
            {
              title: '操作',
              key: 'actions',
              width: 200,
              render: (message: ContactMessage) => (
                <Space>
                  <Tooltip title="查看详情">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeIcon className="h-4 w-4" />}
                      onClick={() => handleViewMessage(message)}
                    />
                  </Tooltip>
                  <Select
                    size="small"
                    style={{ width: 100 }}
                    value={message.status}
                    onChange={(value) => handleStatusChange(message._id, value)}
                  >
                    <Option value="未读">未读</Option>
                    <Option value="已读">已读</Option>
                    <Option value="已回复">已回复</Option>
                    <Option value="已处理">已处理</Option>
                  </Select>
                  <Popconfirm
                    title="确定要删除这条消息吗？"
                    onConfirm={() => handleDelete(message._id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<TrashIcon className="h-4 w-4" />}
                    />
                  </Popconfirm>
                </Space>
              )
            }
          ]}
          dataSource={messages}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 20 }));
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 消息详情模态框 */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <span>消息详情</span>
            {selectedMessage && (
              <Tag color={
                selectedMessage.status === '未读' ? 'red' :
                selectedMessage.status === '已读' ? 'blue' :
                selectedMessage.status === '已回复' ? 'green' : 'purple'
              }>
                {selectedMessage.status}
              </Tag>
            )}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedMessage && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">姓名</label>
                <p className="text-gray-900">{selectedMessage.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">邮箱</label>
                <p className="text-blue-600">{selectedMessage.email}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">主题</label>
              <p className="text-gray-900">{selectedMessage.subject}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">消息内容</label>
              <div className="bg-gray-50 p-4 rounded border max-h-60 overflow-y-auto">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">提交时间</label>
                <p className="text-gray-600">{new Date(selectedMessage.createdAt).toLocaleString('zh-CN')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">IP地址</label>
                <p className="text-gray-600">{selectedMessage.ipAddress || '未知'}</p>
              </div>
            </div>
            {selectedMessage.replyMessage && (
              <div>
                <label className="block text-sm font-medium text-gray-700">回复内容</label>
                <div className="bg-blue-50 p-3 rounded border">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedMessage.replyMessage}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  回复时间：{selectedMessage.replyAt ? new Date(selectedMessage.replyAt).toLocaleString('zh-CN') : ''}
                  {selectedMessage.replyBy && ` | 回复人：${selectedMessage.replyBy.name}`}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactMessageManagement;
